import { useState } from 'react'
import { Popover, Button, Row, Col, Spin } from 'antd'
import { AppstoreOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { fetchTextures, fetchTextureFile } from '$api/evoAPIs'
import PropTypes from 'prop-types'

const TextureItem = ({ texture, onSelect }) => {
  const [hovered, setHovered] = useState(false)

  return (
    <button
      type="button"
      onClick={() => onSelect(texture)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        all: 'unset',
        position: 'relative',
        cursor: 'pointer',
        borderRadius: 8,
        overflow: 'hidden',
        border: hovered ? '2px solid #00566a' : '2px solid transparent',
      }}
    >
      <img
        src={texture.src}
        alt={texture.name}
        style={{ width: '100%', display: 'block' }}
      />
      {hovered && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '4px 8px',
            background: 'rgba(0, 0, 0, 0.7)',
            color: '#fff',
            fontSize: 12,
          }}
        >
          {texture.name}
        </div>
      )}
    </button>
  )
}

TextureItem.propTypes = {
  texture: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
}

const PresetBumpmapPicker = ({ setImageFile }) => {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()

  const fetchPreexistingTextures = async () => {
    const data = await fetchTextures()

    const preexistingTextures = await Promise.all(
      data.map(async (texture) => {
        const imgData = await fetchTextureFile(texture.id)
        return {
          id: texture.id,
          name: texture.name,
          mimetype: texture.meta_file.mimetype,
          ext: texture.meta_file.name.split('.').pop(),
          src: URL.createObjectURL(imgData),
        }
      }),
    )

    return preexistingTextures
  }

  const { data: preexistingTextures, isLoading } = useQuery({
    queryKey: ['textures'],
    queryFn: fetchPreexistingTextures,
  })

  const handleSelect = async (texture) => {
    setOpen(false)

    const response = await fetch(texture.src)
    const blob = await response.blob()
    const file = new File([blob], `${texture.id}.${texture.ext}`, {
      type: texture.mimetype,
    })

    setImageFile({
      file: file,
      isPreset: true,
      presetName: texture.name,
      textureId: texture.id,
    })
  }

  const content = (
    <div style={{ width: 450 }}>
      <Row gutter={[8, 8]}>
        {preexistingTextures?.map((texture) => (
          <Col key={texture.id} span={8}>
            <TextureItem texture={texture} onSelect={handleSelect} />
          </Col>
        ))}
      </Row>
    </div>
  )

  if (isLoading) return <Spin />

  return (
    <Popover
      content={content}
      title={t('service.apply_texturing.choose_preset')}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottom"
    >
      <Button size="large" icon={<AppstoreOutlined style={{ fontSize: 18 }} />}>
        {t('service.apply_texturing.choose_preset')}
      </Button>
    </Popover>
  )
}

PresetBumpmapPicker.propTypes = {
  setImageFile: PropTypes.func.isRequired,
}

export default PresetBumpmapPicker
